// src/components/patterns/PatternDetail.js - Pattern detail modal
import { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useNotification } from '../../hooks/useNotification';
import FriendlyAnalysis from '../common/FriendlyAnalysis';

const PatternDetail = ({ pattern, onClose, onAcknowledge }) => {
  const [affectedReports, setAffectedReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    if (pattern && pattern.affected_report_ids && pattern.affected_report_ids.length > 0) {
      loadAffectedReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pattern]);

  const loadAffectedReports = async () => {
    if (!pattern.affected_report_ids || pattern.affected_report_ids.length === 0) return;

    setLoadingReports(true);
    try {
      // Fetch reports by IDs
      const reportPromises = pattern.affected_report_ids.slice(0, 20).map(id =>
        api.get(`/api/reports/report/${id}`).catch(() => null)
      );
      const results = await Promise.all(reportPromises);
      const reports = results.filter(r => r && r.data && r.data.report).map(r => r.data.report);
      setAffectedReports(reports);
    } catch (error) {
      console.error('Failed to load affected reports:', error);
    } finally {
      setLoadingReports(false);
    }
  };

  const formatPatternType = (type) => {
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const patternData = typeof pattern.pattern_data === 'string' 
    ? JSON.parse(pattern.pattern_data) 
    : pattern.pattern_data;

  const handleAcknowledge = async () => {
    try {
      // TODO: Implement acknowledge endpoint
      // await api.post(`/api/analytics/patterns/${pattern.id}/acknowledge`);
      if (onAcknowledge) {
        onAcknowledge(pattern.id);
      }
      if (showSuccess) {
        showSuccess('Pattern acknowledged');
      }
      onClose();
    } catch (error) {
      console.error('Failed to acknowledge pattern:', error);
      if (showError) {
        showError('Failed to acknowledge pattern');
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
        <div className="p-6 border-b border-gray-200 dark:border-shayek-dark-200 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-shayek-light-900 dark:text-shayek-dark-900">Pattern Details</h3>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-shayek-dark-500 hover:text-gray-600 dark:hover:text-shayek-dark-300 text-2xl transition-colors"
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Pattern Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500 dark:text-shayek-dark-500">Pattern Type</label>
              <p className="text-sm text-gray-900 dark:text-shayek-dark-900 mt-1">{formatPatternType(pattern.pattern_type)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500 dark:text-shayek-dark-500">Severity</label>
              <p className="text-sm text-gray-900 dark:text-shayek-dark-900 mt-1 capitalize">{pattern.severity}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500 dark:text-shayek-dark-500">Detected At</label>
              <p className="text-sm text-gray-900 dark:text-shayek-dark-900 mt-1">
                {new Date(pattern.detected_at).toLocaleString()}
              </p>
            </div>
            {pattern.acknowledged_at && (
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-shayek-dark-500">Acknowledged At</label>
                <p className="text-sm text-gray-900 dark:text-shayek-dark-900 mt-1">
                  {new Date(pattern.acknowledged_at).toLocaleString()}
                </p>
              </div>
            )}
          </div>

          {/* Pattern Data */}
          <div>
            <label className="text-sm font-medium text-gray-500 dark:text-shayek-dark-500">Pattern Details</label>
            <div className="mt-2 p-4 bg-gray-50 dark:bg-shayek-dark-50 rounded-lg border border-gray-200 dark:border-shayek-dark-200">
              <FriendlyAnalysis data={patternData} />
            </div>
          </div>

          {/* Affected Reports */}
          <div>
            <label className="text-sm font-medium text-gray-500 dark:text-shayek-dark-500">
              Affected Reports ({pattern.affected_report_ids?.length || 0})
            </label>
            {loadingReports ? (
              <div className="mt-2 text-center text-gray-500 dark:text-shayek-dark-500 py-4">Loading reports...</div>
            ) : affectedReports.length > 0 ? (
              <div className="mt-2 space-y-2 max-h-64 overflow-y-auto">
                {affectedReports.map((report) => (
                  <div
                    key={report.id}
                    className="p-3 bg-gray-50 dark:bg-shayek-dark-50 rounded-lg hover:bg-gray-100 dark:hover:bg-shayek-dark-100 cursor-pointer border border-gray-200 dark:border-shayek-dark-200 transition-colors"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-1 text-xs font-medium bg-shayek-primary/10 dark:bg-shayek-primary/20 text-shayek-primary dark:text-shayek-red-light rounded">
                        {report.input_type}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        report.status === 'verified' ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400' :
                        report.status === 'rejected' ? 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400' :
                        'bg-gray-100 dark:bg-gray-900/20 text-gray-800 dark:text-gray-400'
                      }`}>
                        {report.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-shayek-dark-700 truncate">{report.input_preview}</p>
                    <p className="text-xs text-gray-500 dark:text-shayek-dark-500 mt-1">
                      {new Date(report.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
                {pattern.affected_report_ids.length > affectedReports.length && (
                  <p className="text-xs text-gray-500 dark:text-shayek-dark-500 text-center py-2">
                    Showing {affectedReports.length} of {pattern.affected_report_ids.length} reports
                  </p>
                )}
              </div>
            ) : (
              <div className="mt-2 text-center text-gray-500 dark:text-shayek-dark-500 py-4">
                No reports loaded
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-shayek-dark-200">
            {!pattern.acknowledged_at && (
              <button
                onClick={handleAcknowledge}
                className="px-4 py-2 text-sm text-white bg-shayek-primary dark:bg-shayek-primary-600 rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light transition-colors shadow-shayek"
              >
                Acknowledge Pattern
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 dark:text-shayek-dark-700 bg-gray-100 dark:bg-shayek-dark-50 rounded-lg hover:bg-gray-200 dark:hover:bg-shayek-dark-100 transition-colors border border-shayek-light-300 dark:border-shayek-dark-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatternDetail;

